import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Stage, Layer, Group, Image as KonvaImage, Line } from "react-konva";
import {
  IconArrowBackUp,
  IconBrush,
  IconCheck,
  IconEraser,
  IconEye,
  IconLoader,
  IconMaximize,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";
import classNames from "classnames";
import { toast } from "react-toastify";
import { motion, AnimatePresence, useAnimationControls } from "framer-motion";
import { Modal } from "@shared/ui/modal/modal";
import s from "./image-editor-modal.module.scss";

const COLORS = ["#F87728", "#E03131", "#1971C2", "#2F9E44", "#FFFFFF", "#000000"];
const MIN_SIZE = 2;
const MAX_SIZE = 20;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 3;
const HINT_KEY = "image-editor-hint-shown";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const ImageEditorModal = ({
  isOpen,
  close,
  src,
  fileName = "image",
  onSave,
}) => {
  const [image, setImage] = useState(null);
  const [lines, setLines] = useState([]);
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(4);
  const [mode, setMode] = useState("brush"); // brush | eraser
  const [isSaving, setIsSaving] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(false);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 }); // scale + pan группы
  const [canvasNode, setCanvasNode] = useState(null);

  const stageRef = useRef(null);
  const groupRef = useRef(null);
  const cursorRef = useRef(null);
  const isDrawing = useRef(false);
  const spaceDown = useRef(false);
  const panState = useRef(null);
  const userTouchedView = useRef(false); // пользователь зумил/панил — не сбрасываем view при ресайзе
  const lastCursorPos = useRef(null);

  // Рефы с актуальными значениями — чтобы не пересоздавать обработчики и не дёргать React-стейт на каждый mousemove
  const viewRef = useRef(view);
  const toolRef = useRef({ mode, size, color });
  useEffect(() => {
    viewRef.current = view;
  }, [view]);
  useEffect(() => {
    toolRef.current = { mode, size, color };
  }, [mode, size, color]);

  const switchMode = useCallback((nextMode) => {
    setMode(nextMode);
    if (navigator.vibrate) navigator.vibrate(10);
  }, []);

  /* ---------- загрузка изображения ---------- */

  useEffect(() => {
    if (!isOpen || !src) return;

    setLines([]);
    setMode("brush");
    setShowOriginal(false);
    setImage(null);
    setView({ scale: 1, x: 0, y: 0 });
    userTouchedView.current = false;

    const img = new window.Image();
    img.onload = () => setImage(img);
    img.src = src;
  }, [isOpen, src]);

  useEffect(() => {
    if (isOpen && image && !localStorage.getItem(HINT_KEY)) {
      setShowHint(true);
    }
  }, [isOpen, image]);

  const dismissHint = useCallback(() => {
    setShowHint(false);
    localStorage.setItem(HINT_KEY, "1");
  }, []);

  /* ---------- размер вьюпорта ---------- */

  /* ---------- размер вьюпорта ----------
     Важно: react-modal монтирует children в портал отдельным коммитом,
     поэтому замер привязан к самому DOM-узлу (callback-ref), а не к isOpen */

  useEffect(() => {
    if (!canvasNode) return;
    const update = () => {
      const rect = canvasNode.getBoundingClientRect();
      setViewport({ width: rect.width, height: rect.height });
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(canvasNode);
    return () => observer.disconnect();
  }, [canvasNode]);

  /* ---------- zoom / pan ---------- */

  const fitView = useMemo(() => {
    if (!image || !viewport.width || !viewport.height) return null;
    const scale = Math.min(
      viewport.width / image.width,
      viewport.height / image.height,
      1
    );
    return {
      scale,
      x: (viewport.width - image.width * scale) / 2,
      y: (viewport.height - image.height * scale) / 2,
    };
  }, [image, viewport]);

  useEffect(() => {
    if (fitView && !userTouchedView.current) {
      setView(fitView);
    }
  }, [fitView]);

  const clampScale = useCallback(
    (scale) =>
      fitView
        ? clamp(scale, fitView.scale * MIN_ZOOM, fitView.scale * MAX_ZOOM)
        : scale,
    [fitView]
  );

  const zoomAt = useCallback((factor, center) => {
    userTouchedView.current = true;
    setView((v) => {
      const next = clampScale(v.scale * factor);
      const cx = center?.x ?? viewport.width / 2;
      const cy = center?.y ?? viewport.height / 2;
      const worldX = (cx - v.x) / v.scale;
      const worldY = (cy - v.y) / v.scale;
      return { scale: next, x: cx - worldX * next, y: cy - worldY * next };
    });
  }, [clampScale, viewport]);

  const resetFit = useCallback(() => {
    userTouchedView.current = false;
    if (fitView) setView(fitView);
  }, [fitView]);

  const handleWheel = useCallback(
    (e) => {
      if (!fitView) return;
      e.evt.preventDefault();
      const pointer = e.target.getStage()?.getPointerPosition();
      zoomAt(e.evt.deltaY > 0 ? 0.9 : 1.1, pointer ?? undefined);
    },
    [fitView, zoomAt]
  );

  const zoomPercent = fitView ? Math.round((view.scale / fitView.scale) * 100) : 100;

  /* ---------- кастомный курсор (через DOM, без ре-рендеров) ---------- */

  const updateCursorDom = useCallback((pos) => {
    const el = cursorRef.current;
    if (!el || !pos) return;
    const { mode: m, size: brushSize, color: brushColor } = toolRef.current;
    const d = Math.max(m === "eraser" ? brushSize * 2.5 : brushSize, 8) * viewRef.current.scale;
    el.style.width = `${d}px`;
    el.style.height = `${d}px`;
    el.style.borderColor = m === "eraser" ? "#8A9099" : brushColor;
    el.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -50%)`;
  }, []);

  useEffect(() => {
    if (cursorVisible) updateCursorDom(lastCursorPos.current);
  }, [mode, size, color, view.scale, cursorVisible, updateCursorDom]);

  /* ---------- рисование ---------- */

  const getGroupPointer = (e) =>
    groupRef.current?.getRelativePointerPosition() ?? null;

  const handlePointerDown = useCallback(
    (e) => {
      dismissHint();
      if (!image || isSaving) return;

      // Панорамирование: средняя кнопка или пробел
      if (e.evt.button === 1 || spaceDown.current) {
        const pos = e.target.getStage()?.getPointerPosition();
        if (!pos) return;
        panState.current = { startX: pos.x, startY: pos.y, viewX: viewRef.current.x, viewY: viewRef.current.y };
        userTouchedView.current = true;
        setIsPanning(true);
        return;
      }

      if (e.evt.button !== 0) return;

      const pos = getGroupPointer(e);
      if (!pos) return;
      isDrawing.current = true;
      const { mode: m, size: brushSize, color: brushColor } = toolRef.current;
      setLines((prev) => [
        ...prev,
        { mode: m, color: brushColor, size: brushSize, points: [pos.x, pos.y] },
      ]);
    },
    [image, isSaving, dismissHint]
  );

  const handlePointerMove = useCallback(
    (e) => {
      const stagePos = e.target.getStage()?.getPointerPosition();
      if (stagePos) {
        lastCursorPos.current = stagePos;
        updateCursorDom(stagePos);
      }

      if (panState.current) {
        const pos = e.target.getStage()?.getPointerPosition();
        if (!pos) return;
        const { startX, startY, viewX, viewY } = panState.current;
        setView((v) => ({ ...v, x: viewX + pos.x - startX, y: viewY + pos.y - startY }));
        return;
      }

      if (!isDrawing.current) return;
      const pos = getGroupPointer(e);
      if (!pos) return;
      setLines((prev) => {
        const last = prev[prev.length - 1];
        if (!last) return prev;
        return [
          ...prev.slice(0, -1),
          { ...last, points: [...last.points, pos.x, pos.y] },
        ];
      });
    },
    [updateCursorDom]
  );

  const handlePointerUp = useCallback(() => {
    isDrawing.current = false;
    panState.current = null;
    setIsPanning(false);
  }, []);

  const handleUndo = useCallback(() => {
    setLines((prev) => prev.slice(0, -1));
  }, []);

  /* ---------- сохранение ---------- */

  const handleSave = useCallback(async () => {
    const group = groupRef.current;
    if (!group || isSaving) return;

    setIsSaving(true);
    try {
      const blob = await group.toBlob({ mimeType: "image/png", quality: 1 });
      const baseName = fileName.replace(/\.\w+$/, "");
      onSave(new File([blob], `${baseName}-edit.png`, { type: "image/png" }));
      close();
    } catch (error) {
      console.error("ImageEditor: failed to export canvas", error);
      toast.error("Не удалось сохранить изображение");
    } finally {
      setIsSaving(false);
    }
  }, [fileName, isSaving, onSave, close]);

  const saveAnim = useAnimationControls();
  const handleSaveClick = useCallback(() => {
    if (isSaving) return;
    if (!lines.length) {
      saveAnim.start({
        x: [0, -6, 6, -4, 4, 0],
        transition: { duration: 0.4, ease: "easeInOut" },
      });
      toast.info("Сначала нарисуйте что-нибудь на изображении");
      return;
    }
    handleSave();
  }, [isSaving, lines.length, handleSave, saveAnim]);

  /* ---------- горячие клавиши ---------- */

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const mod = e.metaKey || e.ctrlKey;

      if (mod && e.key.toLowerCase() === "z") {
        e.preventDefault();
        handleUndo();
      } else if (mod && e.key === "Enter") {
        e.preventDefault();
        handleSaveClick();
      } else if (e.code === "Space") {
        spaceDown.current = true;
        e.preventDefault();
      } else if (!mod && !e.altKey) {
        const k = e.key.toLowerCase();
        if (k === "b") switchMode("brush");
        else if (k === "e") switchMode("eraser");
        else if (e.key === "[") setSize((v) => clamp(v - 1, MIN_SIZE, MAX_SIZE));
        else if (e.key === "]") setSize((v) => clamp(v + 1, MIN_SIZE, MAX_SIZE));
      }
    };
    const onKeyUp = (e) => {
      if (e.code === "Space") spaceDown.current = false;
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      spaceDown.current = false;
    };
  }, [isOpen, handleUndo, handleSaveClick, switchMode]);

  return (
    <Modal
      isOpen={isOpen}
      close={close}
      style={{ content: { padding: 16, maxWidth: "calc(100svw - 32px)" } }}
    >
      <motion.div
        className={s.editor}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        <div className={s.canvasArea} ref={setCanvasNode}>
          {image && viewport.width > 0 ? (
            <motion.div
              className={s.stageHolder}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              <Stage
                ref={stageRef}
                width={viewport.width}
                height={viewport.height}
                className={classNames(
                  s.stage,
                  isPanning ? s.isPanning : ""
                )}
                onMouseDown={handlePointerDown}
                onMouseMove={handlePointerMove}
                onMouseUp={handlePointerUp}
                onMouseLeave={() => {
                  handlePointerUp();
                  setCursorVisible(false);
                }}
                onMouseEnter={() => setCursorVisible(true)}
                onTouchStart={handlePointerDown}
                onTouchMove={handlePointerMove}
                onTouchEnd={handlePointerUp}
                onWheel={handleWheel}
              >
                <Layer>
                  <Group ref={groupRef} x={view.x} y={view.y} scaleX={view.scale} scaleY={view.scale}>
                    <KonvaImage image={image} listening={false} perfectDrawEnabled={false} />
                    <Group visible={!showOriginal} listening={false}>
                      {lines.map((line, i) => (
                        <Line
                          key={i}
                          points={line.points}
                          stroke={line.color}
                          strokeWidth={line.size}
                          lineCap="round"
                          lineJoin="round"
                          tension={0.4}
                          hitStrokeWidth={0}
                          // destination-out стирает всё под линией (картинку тоже) — эффект белого корректора
                          globalCompositeOperation={
                            line.mode === "eraser" ? "destination-out" : "source-over"
                          }
                          listening={false}
                        />
                      ))}
                    </Group>
                  </Group>
                </Layer>
              </Stage>
            </motion.div>
          ) : (
            <div className={s.skeleton} aria-label="Загрузка изображения" />
          )}

          {/* кастомный курсор-кисть */}
          <div
            ref={cursorRef}
            className={classNames(
              s.brushCursor,
              mode === "eraser" ? s.eraserCursor : "",
              cursorVisible && !isPanning && image ? s.visible : ""
            )}
          />

          {/* хинт при первом запуске */}
          <AnimatePresence>
            {showHint && image && (
              <motion.div
                className={s.hint}
                initial={{ opacity: 0, y: 8, x: "-50%" }}
                animate={{ opacity: 1, y: 0, x: "-50%" }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.3 }}
              >
                <motion.span
                  className={s.hintPulse}
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                />
                <span>Рисуйте прямо по изображению</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* зум-контролы */}
          {image && (
            <motion.div
              className={s.zoomControls}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <button type="button" onClick={() => zoomAt(1 / 1.2)} aria-label="Уменьшить" title="Уменьшить">
                <IconZoomOut size={16} />
              </button>
              <span className={s.zoomPercent}>{zoomPercent}%</span>
              <button type="button" onClick={() => zoomAt(1.2)} aria-label="Увеличить" title="Увеличить">
                <IconZoomIn size={16} />
              </button>
              <button type="button" onClick={resetFit} aria-label="По размеру" title="По размеру">
                <IconMaximize size={16} />
              </button>
            </motion.div>
          )}

          {/* отмена и просмотр оригинала — верхний левый угол */}
          {image && (
            <motion.div
              className={s.topControls}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <motion.button
                type="button"
                whileTap={{ scale: 0.9 }}
                className={classNames(s.toolButton, s.topButton)}
                onClick={handleUndo}
                disabled={!lines.length}
                aria-label="Отменить штрих (Ctrl+Z)"
                title="Отменить штрих (Ctrl+Z)"
              >
                <IconArrowBackUp size={18} />
                {lines.length > 0 && <span className={s.undoBadge}>{lines.length}</span>}
              </motion.button>

              <button
                type="button"
                className={classNames(s.originalButton, s.topButton)}
                onPointerDown={() => setShowOriginal(true)}
                onPointerUp={() => setShowOriginal(false)}
                onPointerLeave={() => setShowOriginal(false)}
                aria-label="Удерживайте, чтобы увидеть оригинал"
                title="Удерживайте, чтобы увидеть оригинал"
              >
                <IconEye size={18} />
              </button>
            </motion.div>
          )}

          {/* вуаль при сохранении */}
          <AnimatePresence>
            {isSaving && (
              <motion.div
                className={s.savingVeil}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <IconLoader size={28} className={s.spin} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* плавающий тулбар (без глазморфизма) */}
          {image && (
            <motion.div
              className={s.toolbar}
              initial={{ opacity: 0, y: 12, x: "-50%" }}
              animate={{ opacity: 1, y: 0, x: "-50%" }}
              transition={{ delay: 0.1, ease: "easeOut" }}
            >
              <div className={s.toolbarRow}>
                <div className={s.colors}>
                  {COLORS.map((c) => (
                    <motion.button
                      key={c}
                      type="button"
                      whileTap={{ scale: 1.25 }}
                      className={classNames(
                        s.colorButton,
                        color === c && mode === "brush" ? s.activeColor : "",
                        c === "#FFFFFF" ? s.checker : ""
                      )}
                      style={{ background: c === "#FFFFFF" ? undefined : c }}
                      onClick={() => {
                        setColor(c);
                        switchMode("brush");
                      }}
                      aria-label={`Цвет ${c}`}
                      title={`Цвет ${c}`}
                    />
                  ))}
                </div>
              </div>

              <div className={s.toolbarRow}>
                <motion.button
                  type="button"
                  whileTap={{ scale: 0.9 }}
                  className={classNames(s.toolButton, mode === "brush" ? s.active : "")}
                  onClick={() => switchMode("brush")}
                  aria-label="Кисть (B)"
                  title="Кисть (B)"
                >
                  <IconBrush size={18} />
                </motion.button>
                <motion.button
                  type="button"
                  whileTap={{ scale: 0.9 }}
                  className={classNames(s.toolButton, mode === "eraser" ? s.active : "")}
                  onClick={() => switchMode("eraser")}
                  aria-label="Ластик (E)"
                  title="Ластик (E) — стирает в том числе картинку"
                >
                  <IconEraser size={18} />
                </motion.button>

                <div className={s.sizeControl} title="Толщина линии ([ и ])">
                  <span className={s.sizeLine} style={{ height: 2 }} />
                  <input
                    type="range"
                    min={MIN_SIZE}
                    max={MAX_SIZE}
                    value={size}
                    onChange={(e) => setSize(Number(e.target.value))}
                    className={s.sizeRange}
                    aria-label="Толщина линии"
                  />
                  <span className={s.sizeLine} style={{ height: 6 }} />
                </div>
              </div>
            </motion.div>
          )}
        </div>

        <div className={s.footer}>
          <button
            type="button"
            className={s.cancelButton}
            onClick={close}
            disabled={isSaving}
          >
            Отмена
          </button>
          <motion.button
            type="button"
            animate={saveAnim}
            className={s.saveButton}
            onClick={handleSaveClick}
            disabled={isSaving}
          >
            {isSaving ? (
              <IconLoader size={16} className={s.spin} />
            ) : (
              <IconCheck size={16} />
            )}
            {isSaving ? "Сохранение..." : "Сохранить"}
          </motion.button>
        </div>
      </motion.div>
    </Modal>
  );
};
